import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axios';
import FirmaCanvasModal from '../components/FirmaCanvasModal';
import {
  Table, Button, Input, Modal, Form, Select, message,
  Card, Space, Tag, Tooltip, Badge, Upload, DatePicker, Popconfirm
} from 'antd';
import {
  FileTextOutlined, FileProtectOutlined, PlusOutlined, SearchOutlined, HomeOutlined,
  CopyOutlined, DownloadOutlined, CheckCircleOutlined, ClockCircleOutlined,
  UserOutlined, CarOutlined, UploadOutlined, ImportOutlined,
  EditOutlined, DeleteOutlined, SignatureOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { hssColors, commonStyles } from '../config/theme';
import Footer from '../components/Footer';
import dayjs from 'dayjs';

const { Option } = Select;

const SERVICIO_LABEL = {
  RASTREO: 'Rastreo Satelital',
  VISION: 'HSS Vision',
  RASTREO_VISION: 'Rastreo + Vision',
};

const Contratos = () => {
  const navigate = useNavigate();
  const [contratos, setContratos] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [form] = Form.useForm();
  const [formImportar] = Form.useForm();
  const [modalImportar, setModalImportar] = useState(false);
  const [loadingImportar, setLoadingImportar] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [vehiculosCliente, setVehiculosCliente] = useState([]);
  const [modalEditar, setModalEditar] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formEditar] = Form.useForm();
  const [firmaPresencialContrato, setFirmaPresencialContrato] = useState(false);
  const [contratoPresencial, setContratoPresencial] = useState(null);
  const [savingPresencial, setSavingPresencial] = useState(false);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const [contratosRes, vehiculosRes, clientesRes] = await Promise.all([
        axiosInstance.get('/contratos'),
        axiosInstance.get('/vehiculos'),
        axiosInstance.get('/clientes'),
      ]);
      setContratos(contratosRes.data);
      setVehiculos(vehiculosRes.data);
      setClientes(clientesRes.data);
    } catch {
      message.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const onVehiculoChange = (chapas) => {
    const primerChapa = Array.isArray(chapas) ? chapas[0] : chapas;
    const v = vehiculos.find(v => v.chapa === primerChapa);
    setSelectedVehiculo(v || null);
    if (!v) {
      form.setFieldsValue({ cliente_info: '', cliente_domicilio: '', tipo_contrato: undefined });
      return;
    }
    const cliente = clientes.find(c => c.dni === v.cliente_dni);
    const tipoAutoDetectado = v.servicio === 'VISION' ? 'VISION' : 'RASTREO';
    form.setFieldsValue({
      cliente_info: cliente ? `${cliente.nombre} ${cliente.apellido} — CI/RUC: ${cliente.ruc || cliente.dni}` : '',
      cliente_domicilio: cliente?.domicilio || '',
      tipo_contrato: tipoAutoDetectado,
    });
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const payload = {
        vehiculos_chapas: values.vehiculos_chapas,
        tipo_contrato: values.tipo_contrato,
        vigencia: values.vigencia,
        cliente_domicilio: values.cliente_domicilio,
        servicio_monto: values.servicio_monto,
        servicio_forma_pago: values.servicio_forma_pago,
      };
      const res = await axiosInstance.post('/contratos', payload);
      message.success('Contrato creado exitosamente');
      setModalVisible(false);
      form.resetFields();
      setSelectedVehiculo(null);
      setContratos(prev => [res.data, ...prev]);
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al crear contrato');
    } finally {
      setLoading(false);
    }
  };

  const copiarLink = (url) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => message.success('Link copiado'));
    } else {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand('copy'); message.success('Link copiado'); }
      catch { message.error('No se pudo copiar el link'); }
      document.body.removeChild(ta);
    }
  };

  const descargarPDF = async (id, nombre, chapa) => {
    try {
      const res = await axiosInstance.get(`/contratos/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato_${nombre}_${chapa}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      message.error('Error al descargar PDF');
    }
  };

  const onClienteImportarChange = (dni) => {
    const cliente = clientes.find(c => c.dni === dni);
    setClienteSeleccionado(cliente || null);
    setVehiculosCliente(vehiculos.filter(v => v.cliente_dni === dni));
    if (cliente) {
      formImportar.setFieldsValue({
        cliente_ci_ruc: cliente.ruc || String(cliente.dni),
        cliente_representante: `${cliente.nombre} ${cliente.apellido || ''}`.trim(),
        cliente_domicilio: cliente.domicilio || '',
      });
    }
  };

  const onFinishImportar = async (values) => {
    if (!pdfFile) { message.error('Seleccioná el archivo PDF'); return; }
    try {
      setLoadingImportar(true);
      const fd = new FormData();
      fd.append('pdf', pdfFile);
      fd.append('cliente_dni', values.cliente_dni);
      fd.append('tipo_contrato', values.tipo_contrato);
      fd.append('cliente_ci_ruc', values.cliente_ci_ruc || '');
      fd.append('cliente_representante', values.cliente_representante || '');
      fd.append('cliente_domicilio', values.cliente_domicilio || '');
      fd.append('vigencia', values.vigencia || '');
      fd.append('servicio_monto', values.servicio_monto || '');
      fd.append('servicio_forma_pago', values.servicio_forma_pago || '');
      if (values.fecha_contrato) {
        const d = dayjs(values.fecha_contrato);
        fd.append('fecha_dia', d.format('D'));
        fd.append('fecha_mes', d.format('MMMM'));
        fd.append('fecha_anio', d.format('YYYY'));
      } else {
        const hoy = dayjs();
        fd.append('fecha_dia', hoy.format('D'));
        fd.append('fecha_mes', hoy.format('MMMM'));
        fd.append('fecha_anio', hoy.format('YYYY'));
      }
      if (values.vehiculos_chapas?.length) {
        values.vehiculos_chapas.forEach(ch => fd.append('vehiculos_chapas', ch));
      }
      const rawToken = localStorage.getItem('token') || '';
      const bearerToken = rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/contratos/importar`, {
        method: 'POST',
        headers: { Authorization: bearerToken },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al importar');
      }
      const nuevo = await res.json();
      message.success('Contrato importado exitosamente');
      setModalImportar(false);
      formImportar.resetFields();
      setPdfFile(null);
      setClienteSeleccionado(null);
      setVehiculosCliente([]);
      setContratos(prev => [nuevo, ...prev]);
    } catch (e) {
      message.error(e.message || 'Error al importar contrato');
    } finally {
      setLoadingImportar(false);
    }
  };

  const MESES_NUM = {
    enero: 1, febrero: 2, marzo: 3, abril: 4, mayo: 5, junio: 6,
    julio: 7, agosto: 8, septiembre: 9, octubre: 10, noviembre: 11, diciembre: 12,
  };
  const MESES_ES = { 1: 'enero', 2: 'febrero', 3: 'marzo', 4: 'abril', 5: 'mayo', 6: 'junio',
    7: 'julio', 8: 'agosto', 9: 'septiembre', 10: 'octubre', 11: 'noviembre', 12: 'diciembre' };

  const abrirEditar = (contrato) => {
    setEditando(contrato);
    let fechaContrato = null;
    if (contrato.fecha_dia && contrato.fecha_mes && contrato.fecha_anio) {
      const mesNum = MESES_NUM[contrato.fecha_mes?.toLowerCase()];
      const anio = String(contrato.fecha_anio).length === 2 ? `20${contrato.fecha_anio}` : contrato.fecha_anio;
      if (mesNum) {
        fechaContrato = dayjs(`${anio}-${String(mesNum).padStart(2, '0')}-${String(contrato.fecha_dia).padStart(2, '0')}`);
      }
    }
    formEditar.setFieldsValue({
      tipo_contrato: contrato.tipo_contrato,
      vigencia: contrato.vigencia,
      servicio_monto: contrato.servicio_monto,
      servicio_forma_pago: contrato.servicio_forma_pago,
      cliente_representante: contrato.cliente_representante,
      cliente_ci_ruc: contrato.cliente_ci_ruc,
      cliente_domicilio: contrato.cliente_domicilio,
      fecha_contrato: fechaContrato,
    });
    setModalEditar(true);
  };

  const onFinishEditar = async (values) => {
    try {
      const payload = { ...values };
      if (values.fecha_contrato) {
        const d = dayjs(values.fecha_contrato);
        payload.fecha_dia = String(d.date());
        payload.fecha_mes = MESES_ES[d.month() + 1];
        payload.fecha_anio = String(d.year()).slice(2);
      }
      delete payload.fecha_contrato;
      const res = await axiosInstance.put(`/contratos/${editando.id}`, payload);
      message.success('Contrato actualizado');
      setContratos(prev => prev.map(c => c.id === editando.id ? res.data : c));
      setModalEditar(false);
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al actualizar');
    }
  };

  const eliminarContrato = async (id) => {
    try {
      await axiosInstance.delete(`/contratos/${id}`);
      message.success('Contrato eliminado');
      setContratos(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al eliminar');
    }
  };

  const firmarContratoPresencial = async (firmaData) => {
    if (!contratoPresencial) return;
    setSavingPresencial(true);
    try {
      await axiosInstance.post(`/contratos/${contratoPresencial.id}/firmar-presencial`, {
        signature_image: firmaData,
      });
      setContratos(prev => prev.map(c =>
        c.id === contratoPresencial.id
          ? { ...c, estado: 'FIRMADO', signed_at: new Date().toISOString() }
          : c
      ));
      setFirmaPresencialContrato(false);
      setContratoPresencial(null);
      message.success('Contrato firmado exitosamente');
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al firmar contrato');
    } finally {
      setSavingPresencial(false);
    }
  };

  const filteredContratos = contratos.filter(c => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (
      c.cliente_representante?.toLowerCase().includes(q) ||
      c.vehiculo_chapa?.toLowerCase().includes(q) ||
      c.vehiculo_marca?.toLowerCase().includes(q) ||
      c.tipo_contrato?.toLowerCase().includes(q)
    );
  });

  const columns = [
    {
      title: 'N° Contrato',
      dataIndex: 'id',
      key: 'nro',
      width: 130,
      sorter: (a, b) => (a.id || '').localeCompare(b.id || ''),
      render: (id) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: hssColors.primary, fontSize: 13 }}>
          #{id?.slice(0, 8).toUpperCase()}
        </span>
      ),
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente_representante',
      key: 'cliente',
      sorter: (a, b) => (a.cliente_representante || '').localeCompare(b.cliente_representante || ''),
      render: (v) => <Space><UserOutlined style={{ color: hssColors.primary }} /><span>{v}</span></Space>,
    },
    {
      title: 'Vehículos',
      key: 'vehiculos',
      render: (_, r) => {
        const lista = r.vehiculos?.length ? r.vehiculos : [{ chapa: r.vehiculo_chapa, marca: r.vehiculo_marca, modelo: r.vehiculo_modelo }];
        return (
          <Space direction="vertical" size={2}>
            {lista.map(v => (
              <Space key={v.chapa} size={4}>
                <CarOutlined style={{ color: hssColors.secondary, fontSize: 12 }} />
                <strong style={{ fontSize: 12 }}>{v.chapa}</strong>
                <span style={{ fontSize: 11, color: hssColors.textSecondary }}>{v.marca} {v.modelo}</span>
              </Space>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo_contrato',
      key: 'tipo',
      sorter: (a, b) => (a.tipo_contrato || '').localeCompare(b.tipo_contrato || ''),
      render: (t) => (
        <Tag color={t === 'RASTREO' ? '#3498db' : '#e74c3c'}>
          {t === 'RASTREO' ? 'Rastreo Satelital' : 'HSS Vision'}
        </Tag>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      sorter: (a, b) => (a.estado || '').localeCompare(b.estado || ''),
      render: (e, r) => e === 'FIRMADO'
        ? <Space><Badge status="success" text={<span style={{ color: hssColors.success, fontWeight: 600 }}>Firmado</span>} />{r.origen === 'IMPORTADO' && <Tag color="purple" style={{ fontSize: 10 }}>Importado</Tag>}</Space>
        : <Badge status="warning" text={<span style={{ color: hssColors.warning, fontWeight: 600 }}>Pendiente</span>} />,
    },
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      key: 'fecha',
      sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      render: (d) => d ? new Date(d).toLocaleDateString('es-PY') : '-',
    },
    {
      title: 'Vigencia restante',
      key: 'vigencia_restante',
      render: (_, r) => {
        if (r.estado !== 'FIRMADO' || !r.signed_at || !r.vigencia) return <span style={{ color: '#ccc' }}>—</span>;
        const mesesMap = { '6 meses': 6, '12 meses': 12, '24 meses': 24, '36 meses': 36 };
        const meses = mesesMap[r.vigencia] || parseInt((r.vigencia || '').match(/\d+/)?.[0] || '0', 10);
        if (!meses) return <Tag color="default">{r.vigencia || 'Indefinida'}</Tag>;
        const vence = new Date(r.signed_at);
        vence.setMonth(vence.getMonth() + meses);
        const dias = Math.round((vence - new Date()) / 86400000);
        if (dias < 0) return <Tag color="red">Vencido hace {Math.abs(dias)}d</Tag>;
        if (dias <= 30) return <Tag color="orange">{dias}d restantes</Tag>;
        if (dias <= 60) return <Tag color="gold">{dias}d restantes</Tag>;
        return <Tag color="green">{dias}d restantes</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, r) => (
        <Space>
          {r.estado === 'PENDIENTE' && (
            <>
              <Tooltip title="Copiar link de firma para el cliente">
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => copiarLink(r.sign_url)}
                  size="small"
                  style={{ borderRadius: 6 }}
                >
                  Copiar link
                </Button>
              </Tooltip>
              <Tooltip title="Firmar directamente con el cliente presente">
                <Button
                  icon={<SignatureOutlined />}
                  onClick={() => { setContratoPresencial(r); setFirmaPresencialContrato(true); }}
                  size="small"
                  style={{ borderRadius: 6 }}
                >
                  Firmar
                </Button>
              </Tooltip>
              <Tooltip title="Compartir link por WhatsApp">
                <Button
                  size="small"
                  style={{ borderRadius: 6, background: '#25d366', borderColor: '#25d366', color: '#fff' }}
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent('Le comparto el link para firmar su contrato de servicio HSS: ' + r.sign_url)}`, '_blank')}
                >
                  📱 WA
                </Button>
              </Tooltip>
            </>
          )}
          {r.estado === 'FIRMADO' && (
            <Tooltip title="Descargar PDF firmado">
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => descargarPDF(r.id, r.cliente_representante.replace(' ', '_'), r.vehiculo_chapa)}
                size="small"
                style={{ borderRadius: 6 }}
              >
                PDF
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Editar contrato">
            <Button
              icon={<EditOutlined />}
              size="small"
              style={{ borderRadius: 6 }}
              onClick={() => abrirEditar(r)}
            />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar este contrato?"
            description="Esta acción no se puede deshacer."
            onConfirm={() => eliminarContrato(r.id)}
            okText="Sí, eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar contrato">
              <Button icon={<DeleteOutlined />} size="small" danger style={{ borderRadius: 6 }} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <FileProtectOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Contratos Digitales</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>Generación y firma de contratos de servicio</p>
            </div>
          </div>
          <Space>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
            <Button icon={<ImportOutlined />} onClick={() => setModalImportar(true)} size="large" style={{ height: 44, borderRadius: 8, fontWeight: 600 }}>
              Importar firmado
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)} size="large" style={{ height: 44, borderRadius: 8, fontWeight: 600 }}>
              Nuevo Contrato
            </Button>
          </Space>
        </div>
      </div>

      {/* Contenido */}
      <div style={{ padding: '24px 32px', flex: 1 }}>
        {/* Stats rápidas */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total contratos', value: contratos.length, icon: <FileTextOutlined />, color: hssColors.primary },
            { label: 'Pendientes de firma', value: contratos.filter(c => c.estado === 'PENDIENTE').length, icon: <ClockCircleOutlined />, color: hssColors.warning },
            { label: 'Firmados', value: contratos.filter(c => c.estado === 'FIRMADO').length, icon: <CheckCircleOutlined />, color: hssColors.success },
          ].map(s => (
            <Card key={s.label} loading={loading} style={{ flex: 1, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 28, color: s.color }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: hssColors.textSecondary }}>{s.label}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Búsqueda */}
        <Card style={{ marginBottom: 24, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }} styles={{ body: { padding: '20px 24px' } }}>
          <Input
            placeholder="Buscar por cliente, vehículo, tipo..."
            prefix={<SearchOutlined style={{ color: hssColors.gray400 }} />}
            style={{ width: '100%', borderRadius: 8 }}
            size="large"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
          />
        </Card>

        {/* Tabla */}
        <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }} styles={{ body: { padding: 0 } }}>
          <Table
            columns={columns}
            dataSource={filteredContratos}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{ current: paginaActual, pageSize, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], onChange: (p, size) => { setPaginaActual(p); setPageSize(size); }, showTotal: t => `Total: ${t} contratos` }}
            style={{ borderRadius: 12, overflow: 'hidden' }}
          />
        </Card>
      </div>

      <Footer />
      {/* Modal Importar Contrato Firmado */}
      <Modal
        title={
          <Space style={{ fontSize: 18, fontWeight: 700, color: hssColors.primary }}>
            <ImportOutlined /><span>Importar Contrato Firmado</span>
          </Space>
        }
        open={modalImportar}
        onCancel={() => { setModalImportar(false); formImportar.resetFields(); setPdfFile(null); setClienteSeleccionado(null); setVehiculosCliente([]); }}
        footer={null}
        width={680}
      >
        <Form form={formImportar} onFinish={onFinishImportar} layout="vertical" style={{ marginTop: 16 }}>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="cliente_dni" label="Cliente" rules={[{ required: true, message: 'Seleccioná el cliente' }]}>
              <Select
                size="large" showSearch placeholder="Buscar cliente..."
                optionFilterProp="label" onChange={onClienteImportarChange}
              >
                {clientes.map(c => (
                  <Option key={c.dni} value={c.dni} label={`${c.nombre} ${c.apellido || ''} ${c.dni}`}>
                    {c.nombre} {c.apellido || ''} <span style={{ color: hssColors.textSecondary, fontSize: 11 }}>({c.dni})</span>
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="tipo_contrato" label="Tipo de contrato" rules={[{ required: true }]}>
              <Select size="large" placeholder="Tipo">
                <Option value="RASTREO"><Tag color="#3498db">Rastreo Satelital</Tag></Option>
                <Option value="VISION"><Tag color="#e74c3c">HSS Vision</Tag></Option>
              </Select>
            </Form.Item>
          </div>

          {clienteSeleccionado && (
            <>
              <Form.Item name="vehiculos_chapas" label="Vehículos del contrato (opcional)">
                <Select mode="multiple" size="large" placeholder="Seleccioná vehículos" maxTagCount={3}>
                  {vehiculosCliente.map(v => (
                    <Option key={v.chapa} value={v.chapa}><strong>{v.chapa}</strong> — {v.marca} {v.modelo}</Option>
                  ))}
                </Select>
              </Form.Item>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <Form.Item name="cliente_ci_ruc" label="CI / RUC" rules={[{ required: true }]}>
                  <Input size="large" style={{ borderRadius: 8 }} />
                </Form.Item>
                <Form.Item name="fecha_contrato" label="Fecha del contrato">
                  <DatePicker size="large" style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Hoy" />
                </Form.Item>
              </div>

              <Form.Item name="cliente_representante" label="Nombre completo / Representante" rules={[{ required: true }]}>
                <Input size="large" style={{ borderRadius: 8 }} />
              </Form.Item>

              <Form.Item name="cliente_domicilio" label="Domicilio" rules={[{ required: true }]}>
                <Input size="large" placeholder="Calle, número, ciudad" style={{ borderRadius: 8 }} />
              </Form.Item>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <Form.Item name="servicio_monto" label="Monto mensual por vehículo (Gs.)">
                  <Input size="large" placeholder="Ej: 200.000" style={{ borderRadius: 8 }} />
                </Form.Item>
                <Form.Item name="servicio_forma_pago" label="Forma de pago">
                  <Select size="large" placeholder="Forma de pago">
                    <Option value="mensual">Mensual</Option>
                    <Option value="trimestral">Trimestral</Option>
                    <Option value="semestral">Semestral</Option>
                    <Option value="anual">Anual</Option>
                  </Select>
                </Form.Item>
              </div>

              <Form.Item name="vigencia" label="Vigencia" initialValue="12 meses">
                <Select size="large">
                  <Option value="6 meses">6 meses</Option>
                  <Option value="12 meses">12 meses (1 año)</Option>
                  <Option value="24 meses">24 meses (2 años)</Option>
                  <Option value="36 meses">36 meses (3 años)</Option>
                  <Option value="Indefinida">Indefinida</Option>
                </Select>
              </Form.Item>
            </>
          )}

          <Form.Item label="Archivo PDF (contrato escaneado)" required>
            <Upload
              accept=".pdf"
              maxCount={1}
              beforeUpload={file => { setPdfFile(file); return false; }}
              onRemove={() => setPdfFile(null)}
              fileList={pdfFile ? [{ uid: '-1', name: pdfFile.name, status: 'done' }] : []}
            >
              <Button icon={<UploadOutlined />} size="large" style={{ borderRadius: 8 }}>
                {pdfFile ? pdfFile.name : 'Seleccionar PDF firmado'}
              </Button>
            </Upload>
            {!pdfFile && <div style={{ fontSize: 12, color: hssColors.textSecondary, marginTop: 4 }}>Solo archivos PDF</div>}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button size="large" onClick={() => { setModalImportar(false); formImportar.resetFields(); setPdfFile(null); setClienteSeleccionado(null); setVehiculosCliente([]); }}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={loadingImportar} size="large" style={{ borderRadius: 8, fontWeight: 600 }}>
                Importar contrato
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Editar Contrato */}
      <Modal
        title={<Space style={{ fontSize: 18, fontWeight: 700, color: hssColors.primary }}><EditOutlined /><span>Editar Contrato</span></Space>}
        open={modalEditar}
        onCancel={() => setModalEditar(false)}
        footer={null}
        width={560}
      >
        <Form form={formEditar} onFinish={onFinishEditar} layout="vertical" style={{ marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="tipo_contrato" label="Tipo de contrato" rules={[{ required: true }]}>
              <Select size="large">
                <Option value="RASTREO"><Tag color="#3498db">Rastreo Satelital</Tag></Option>
                <Option value="VISION"><Tag color="#e74c3c">HSS Vision</Tag></Option>
              </Select>
            </Form.Item>
            <Form.Item name="vigencia" label="Vigencia">
              <Select size="large">
                <Option value="6 meses">6 meses</Option>
                <Option value="12 meses">12 meses (1 año)</Option>
                <Option value="24 meses">24 meses (2 años)</Option>
                <Option value="36 meses">36 meses (3 años)</Option>
                <Option value="Indefinida">Indefinida</Option>
              </Select>
            </Form.Item>
          </div>
          <Form.Item name="fecha_contrato" label="Fecha del contrato">
            <DatePicker size="large" style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Sin cambios" />
          </Form.Item>
          <Form.Item name="cliente_representante" label="Nombre / Representante" rules={[{ required: true }]}>
            <Input size="large" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item name="cliente_ci_ruc" label="CI / RUC" rules={[{ required: true }]}>
            <Input size="large" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item name="cliente_domicilio" label="Domicilio" rules={[{ required: true }]}>
            <Input size="large" style={{ borderRadius: 8 }} />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="servicio_monto" label="Monto mensual por vehículo (Gs.)" rules={[{ required: true }]}>
              <Input size="large" style={{ borderRadius: 8 }} />
            </Form.Item>
            <Form.Item name="servicio_forma_pago" label="Forma de pago" rules={[{ required: true }]}>
              <Select size="large">
                <Option value="mensual">Mensual</Option>
                <Option value="trimestral">Trimestral</Option>
                <Option value="semestral">Semestral</Option>
                <Option value="anual">Anual</Option>
              </Select>
            </Form.Item>
          </div>
          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button size="large" onClick={() => setModalEditar(false)}>Cancelar</Button>
              <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, fontWeight: 600 }}>Guardar cambios</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <FirmaCanvasModal
        open={firmaPresencialContrato}
        onClose={() => { setFirmaPresencialContrato(false); setContratoPresencial(null); }}
        onConfirm={firmarContratoPresencial}
        loading={savingPresencial}
        titulo={`Firma presencial — ${contratoPresencial?.cliente_representante || ''}`}
      />

      {/* Modal Nuevo Contrato */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 20, fontWeight: 700, color: hssColors.primary }}>
            <FileTextOutlined /><span>Nuevo Contrato</span>
          </div>
        }
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); setSelectedVehiculo(null); }}
        footer={null}
        width={640}
      >
        <Form form={form} onFinish={onFinish} layout="vertical" style={{ marginTop: 24 }}>

          <Form.Item name="vehiculos_chapas" label="Vehículos (puede seleccionar varios)" rules={[{ required: true, message: 'Seleccione al menos un vehículo' }]}>
            <Select
              mode="multiple"
              placeholder="Seleccione uno o más vehículos"
              size="large"
              showSearch
              optionFilterProp="label"
              onChange={onVehiculoChange}
              maxTagCount={3}
            >
              {vehiculos.map(v => {
                const cfg = { RASTREO: '#3498db', VISION: '#e74c3c', RASTREO_VISION: '#9b59b6' };
                const contratoVigente = contratos.find(c =>
                  (c.vehiculos?.some(cv => cv.chapa === v.chapa) || c.vehiculo_chapa === v.chapa) &&
                  (c.estado === 'PENDIENTE' || c.estado === 'FIRMADO')
                );
                return (
                  <Option key={v.chapa} value={v.chapa} label={`${v.chapa} ${v.marca} ${v.modelo || ''}`}>
                    <Space>
                      {v.servicio && <Tag color={cfg[v.servicio]} style={{ fontSize: 10 }}>{SERVICIO_LABEL[v.servicio]}</Tag>}
                      <strong>{v.chapa}</strong> — {v.marca} {v.modelo || ''}
                      {contratoVigente && (
                        <Tag color={contratoVigente.estado === 'FIRMADO' ? 'success' : 'warning'} style={{ fontSize: 10 }}>
                          {contratoVigente.estado === 'FIRMADO' ? '✓ Firmado' : '⏳ Pendiente'} #{contratoVigente.id?.slice(0,6).toUpperCase()}
                        </Tag>
                      )}
                    </Space>
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          {selectedVehiculo && (
            <>
              <Form.Item name="cliente_info" label="Cliente (detectado del primer vehículo)">
                <Input size="large" disabled style={{ borderRadius: 8, background: '#f5f7fa' }} />
              </Form.Item>

              <Form.Item name="tipo_contrato" label="Tipo de contrato" rules={[{ required: true, message: 'Seleccione el tipo de contrato' }]}>
                <Select size="large" placeholder="Seleccione el tipo de contrato a generar">
                  <Option value="RASTREO"><Tag color="#3498db">Rastreo Satelital</Tag></Option>
                  <Option value="VISION"><Tag color="#e74c3c">HSS Vision (Cámaras)</Tag></Option>
                </Select>
              </Form.Item>

              <Form.Item name="vigencia" label="Vigencia del contrato" rules={[{ required: true, message: 'Seleccione la vigencia' }]} initialValue="12 meses">
                <Select size="large">
                  <Option value="6 meses">6 meses</Option>
                  <Option value="12 meses">12 meses (1 año)</Option>
                  <Option value="24 meses">24 meses (2 años)</Option>
                  <Option value="36 meses">36 meses (3 años)</Option>
                  <Option value="Indefinida">Indefinida</Option>
                </Select>
              </Form.Item>

              <Form.Item name="cliente_domicilio" label="Domicilio del cliente" rules={[{ required: true, message: 'Ingrese el domicilio' }]}>
                <Input size="large" placeholder="Calle, número, ciudad" style={{ borderRadius: 8 }} />
              </Form.Item>

              <Form.Item name="servicio_monto" label="Monto mensual por vehículo (Gs.)" rules={[{ required: true, message: 'Ingrese el monto' }]}>
                <Input size="large" placeholder="Ej: 200.000" style={{ borderRadius: 8 }} />
              </Form.Item>

              <Form.Item name="servicio_forma_pago" label="Forma de pago" rules={[{ required: true, message: 'Seleccione la forma de pago' }]}>
                <Select size="large" placeholder="Seleccione la forma de pago">
                  <Option value="mensual">Mensual</Option>
                  <Option value="trimestral">Trimestral</Option>
                  <Option value="semestral">Semestral</Option>
                  <Option value="anual">Anual</Option>
                </Select>
              </Form.Item>
            </>
          )}

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setModalVisible(false); form.resetFields(); setSelectedVehiculo(null); }} size="large" style={{ borderRadius: 8 }}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} size="large" style={{ borderRadius: 8, fontWeight: 600 }}>
                Generar Contrato
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Contratos;
